import { Injectable } from '@angular/core';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Platform } from '@ionic/angular';
import { MediaType } from '@models/imported/SagaBase';
import { SagaSettingsService } from '@services/settings';
import { MediaSource } from '../../schema/enums/media-source';
import { ICustomFile } from '../../schema/interfaces/ICustomFile';
import { CameraBridgeService } from '../camera-bridge/camera-bridge.service';
import { FileReaderService } from '../file-reader/file-reader.service';

@Injectable()
export class FileChooserService {
  constructor(
    private platform: Platform,
    private cameraBridge: CameraBridgeService,
    private fileReader: FileReaderService,
    private sagaSettings: SagaSettingsService
  ) {}

  choose(mediaSource: MediaSource): Promise<ICustomFile> {
    return this.platform.is('cordova') ? this.fromNative(mediaSource) : this.fromInput(mediaSource);
  }

  private fromNative(mediaSource: MediaSource): Promise<ICustomFile> {
    switch (mediaSource) {
      case MediaSource.Camera:
      case MediaSource.Gallery:
        return this.cameraBridge.getPicture(mediaSource);

      case MediaSource.Video:
        return this.cameraBridge.captureVideo();

      case MediaSource.File:
        return this.pickFile();

      default:
        return Promise.reject();
    }
  }

  private pickFile(): Promise<ICustomFile> {
    return FilePicker.pickFiles({
      limit: 1,
      types: this.getAcceptedFileTypes()
    }).then(result => {
      const pickedFile = result.files[0];
      const customFile: ICustomFile = {
        uri: pickedFile.path,
        url: null,
        file: null,
        parent: null,
        dataType: MediaType.Document,
        name: pickedFile.name
      };

      return customFile;
    });
  }

  private getAcceptedFileTypes(): string[] {
    return this.sagaSettings.getValue('SagaMobileWebClient.AllowedMimeTypes');
  }

  private fromInput(mediaSource: MediaSource): Promise<ICustomFile> {
    return new Promise<ICustomFile>((resolve, reject) => {
      const input = this.createInput(mediaSource, resolve, reject);
      input.click();
    });
  }

  private createInput(
    mediaSource: MediaSource,
    resolve: (value: ICustomFile | PromiseLike<ICustomFile>) => void,
    reject: (reason?) => void
  ): HTMLInputElement {
    const input: HTMLInputElement = document.createElement('input');

    input.setAttribute('hidden', '');
    input.setAttribute('type', 'file');

    switch (mediaSource) {
      case MediaSource.Camera:
        input.setAttribute('accept', 'image/*');
        input.setAttribute('capture', 'camera');
        break;

      case MediaSource.Video:
        input.setAttribute('accept', 'video/*');
        input.setAttribute('capture', 'camcorder');
        break;

      case MediaSource.File:
        input.setAttribute('accept', this.getAcceptedFileTypes()?.join(','));
        break;
    }

    input.addEventListener('change', () => {
      this.fileReader.readFromInput(input.files).then(customFile => resolve(customFile));
    });
    input.addEventListener('cancel', () => {
      document.body.removeChild(input);
      reject('User canceled the file picker dialog');
    });

    document.body.appendChild(input);

    return input;
  }
}
